<template>
  <footer class="footer">
    <div class="container">
      <div class="footer__grid">
        <div class="footer__brand">
          <div class="footer__logo">
            <svg width="24" height="24" viewBox="0 0 28 28" fill="none">
              <circle cx="14" cy="14" r="12" stroke="#04213b" stroke-width="2" fill="none"/>
              <path d="M8 14c0-3.3 2.7-8 6-8s6 4.7 6 8-2.7 8-6 8" stroke="#04213b" stroke-width="2" fill="none"/>
            </svg>
            <span>{{ SITE.shortName }}</span>
          </div>
          <h3 class="footer__headline">Get Ready To Create Great</h3>
          <form class="footer__subscribe" @submit.prevent="handleSubscribe">
            <input
              v-model="email"
              type="email"
              placeholder="Email Address"
              required
              class="footer__input"
            />
            <button type="submit" class="footer__submit" aria-label="Subscribe">
              <v-icon size="18">mdi-email-outline</v-icon>
            </button>
          </form>
        </div>

        <div class="footer__col">
          <h4 class="footer__title">Quick Link</h4>
          <nav class="footer__links">
            <button
              v-for="link in FOOTER_LINKS"
              :key="link.id"
              type="button"
              class="footer__link"
              @click="goTo(link.id)"
            >
              {{ link.label }}
            </button>
          </nav>
        </div>

        <div class="footer__col">
          <h4 class="footer__title">Contact</h4>
          <div class="footer__contacts">
            <a :href="`mailto:${SITE.email}`" class="footer__contact">
              <span class="footer__contact-icon"><v-icon size="16">mdi-email-outline</v-icon></span>
              {{ SITE.email }}
            </a>
            <span class="footer__contact">
              <span class="footer__contact-icon"><v-icon size="16">mdi-map-marker-outline</v-icon></span>
              {{ SITE.location }}
            </span>
            <a :href="`tel:${SITE.phone}`" class="footer__contact">
              <span class="footer__contact-icon"><v-icon size="16">mdi-phone-outline</v-icon></span>
              {{ SITE.phone }}
            </a>
          </div>
          <div class="footer__socials">
            <a
              v-for="link in SOCIAL_LINKS"
              :key="link.icon"
              :href="link.url"
              class="footer__social"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="link.label"
            >
              <v-icon size="16">{{ link.icon }}</v-icon>
            </a>
          </div>
        </div>
      </div>

      <hr class="footer__rule" />

      <div class="footer__bottom">
        <p>© {{ year }} {{ SITE.name }}. All Rights Reserved</p>
        <div class="footer__legal">
          <a href="#">Terms & Condition</a>
          <a href="#">Privacy Policy</a>
          <button type="button" class="footer__legal-btn" @click="goTo('contact')">Contact Us</button>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { ref, computed } from "vue";
import { SITE, SOCIAL_LINKS, FOOTER_LINKS } from "@/constants/site";
import { useNavigation } from "@/composables/useNavigation";

const { navigateToSection } = useNavigation();
const email = ref("");
const year = computed(() => new Date().getFullYear());

const goTo = (id) => navigateToSection(id);

const handleSubscribe = () => {
  window.location.href = `mailto:${SITE.email}?subject=Newsletter Subscribe&body=Email: ${email.value}`;
  email.value = "";
};
</script>

<style scoped>
.footer {
  background: var(--color-bg);
  border-top: 1px solid var(--color-border);
  padding: clamp(3rem, 6vw, 5rem) 0 2rem;
}

.footer__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;
}

@media (min-width: 768px) {
  .footer__grid {
    grid-template-columns: 1.5fr 1fr 1.2fr;
    gap: 3rem;
  }
}

.footer__logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
}

.footer__headline {
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 700;
  line-height: 1.2;
  margin: 0 0 1.5rem;
}

.footer__subscribe {
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--color-border);
  max-width: 320px;
}

.footer__input {
  flex: 1;
  background: none;
  border: none;
  font-family: var(--font-sans);
  font-size: 0.875rem;
  color: var(--color-text);
  padding: 0.75rem 0;
  outline: none;
}

.footer__input::placeholder {
  color: var(--color-text-faint);
}

.footer__submit {
  background: none;
  border: none;
  color: var(--color-text-muted);
  cursor: pointer;
  padding: 0.5rem;
  transition: color var(--transition);
}

.footer__submit:hover {
  color: var(--color-accent);
}

.footer__title {
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 1.25rem;
}

.footer__links {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.footer__link {
  background: none;
  border: none;
  text-align: left;
  font-family: var(--font-sans);
  font-size: 0.875rem;
  color: var(--color-text-muted);
  cursor: pointer;
  padding: 0;
  transition: color var(--transition);
}

.footer__link:hover {
  color: var(--color-accent);
}

.footer__contacts {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  margin-bottom: 1.5rem;
}

.footer__contact {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: var(--color-text-muted);
  transition: color var(--transition);
}

a.footer__contact:hover {
  color: var(--color-accent);
}

.footer__contact-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-full);
  border: 1px solid var(--color-border);
  flex-shrink: 0;
}

.footer__socials {
  display: flex;
  gap: 0.5rem;
}

.footer__social {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: var(--radius-full);
  border: 1px solid var(--color-border);
  color: var(--color-text-muted);
  transition: all var(--transition);
}

.footer__social:hover {
  background: var(--color-accent);
  border-color: var(--color-accent);
  color: var(--color-text);
}

.footer__rule {
  border: none;
  border-top: 1px solid var(--color-border);
  margin: 2.5rem 0 1.5rem;
}

.footer__bottom {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  font-size: 0.8125rem;
  color: var(--color-text-faint);
}

.footer__legal {
  display: flex;
  gap: 1.5rem;
}

.footer__legal a,
.footer__legal-btn {
  color: var(--color-text-faint);
  background: none;
  border: none;
  font-family: var(--font-sans);
  font-size: 0.8125rem;
  cursor: pointer;
  padding: 0;
  transition: color var(--transition);
}

.footer__legal a:hover,
.footer__legal-btn:hover {
  color: var(--color-accent);
}
</style>
